package com.iregained.site.components.sections.news.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.news.styles.*
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.region.activeStrings
import com.iregained.site.theme.SubheadlineTextStyle
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.ObfuscatedEmailText
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

// UNIQUE ANIMATION: Camera Shutter Snap (Fast, snappy scale-down)
@Composable
private fun Modifier.shutterSnap(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).scale(if (isVisible) 1f else 1.05f).transition(
        Transition.of("opacity", 0.4.s, TransitionTimingFunction.EaseOut, delayMs.ms),
        Transition.of("transform", 0.4.s, TransitionTimingFunction.EaseOut, delayMs.ms)
    )
}

@Composable
fun NewsPressKitSection(data: NewsPressKitData = defaultNewsPressKitData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "news-press-kit-section"
    var isVisible by remember { mutableStateOf(false) }

    if (data.sectionTitle.isNotBlank()) {
        Column(
            modifier = PressKitContainerStyle.toModifier().id(targetId).fillMaxWidth()
                .background(palette.brand.primarySoft)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") },
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex).justifyContent(JustifyContent.Center)
                    .toAttrs {
                        ref { element ->
                            val observer = IntersectionObserver { entries, obs ->
                                entries.forEach {
                                    if (it.isIntersecting) {
                                        isVisible = true; obs.unobserve(element)
                                    }
                                }
                            }
                            observer.observe(element); onDispose { observer.disconnect() }
                        }
                    }) {
                Column(
                    modifier = Modifier.fillMaxWidth().maxWidth(1440.px),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    SectionHeader(
                        text = data.sectionTitle,
                        fontSize = 32.px,
                        lineWidthPercent = 25.percent,
                        modifier = Modifier.margin(bottom = 16.px).shutterSnap(isVisible, 0),
                        alignment = Alignment.CenterHorizontally
                    )

                    if (data.sectionSubtitle.isNotBlank()) {
                        Div(
                            attrs = SubheadlineTextStyle.toModifier().maxWidth(1200.px).fontSize(18.px)
                                .fontWeight(FontWeight.Medium).color(palette.custom.darkText)
                                .textAlign(TextAlign.Center).margin(bottom = 60.px).shutterSnap(isVisible, 150)
                                .toAttrs {
                                    ref { htmlElement ->
                                        htmlElement.innerHTML = data.sectionSubtitle; onDispose {}
                                    }
                                })
                    }

                    SimpleGrid(numColumns(1, lg = 2), modifier = PressKitGridStyle.toModifier()) {
                        Column(modifier = BoilerplateColumnStyle.toModifier().shutterSnap(isVisible, 300)) {
                            if (data.companyBoilerplate.isNotBlank()) Div(
                                attrs = BoilerplateTextStyle.toModifier().color(palette.custom.darkText).toAttrs {
                                    ref { htmlElement ->
                                        htmlElement.innerHTML = data.companyBoilerplate; onDispose {}
                                    }
                                })
                            if (data.mediaContactEmail.isNotBlank()) {
                                Column(modifier = MediaContactBoxStyle.toModifier()) {
                                    SpanText(
                                        text = "Media & Press Inquiries", modifier = MediaContactLabelStyle.toModifier()
                                    )

                                    // REPLACED SILK LINK WITH DYNAMIC A-TAG
                                    A(
                                        attrs = MediaContactLinkStyle.toModifier()
                                            .cursor(Cursor.Pointer)
                                            .onClick { e ->
                                                e.preventDefault()
                                                kotlinx.browser.window.location.assign("mailto:${data.mediaContactEmail}")
                                            }
                                            .toAttrs { attr("role", "link"); attr("tabindex", "0") }
                                    ) {
                                        // APPLY OBFUSCATION TO THE VISUAL TEXT HERE:
                                        ObfuscatedEmailText(email = data.mediaContactEmail)
                                    }
                                }
                            }
                        }

                        Column(modifier = AssetListColumnStyle.toModifier()) {
                            data.assets.forEachIndexed { index, asset ->
                                if (asset.title.isNotBlank()) {
                                    key(asset.title) {
                                        Link(
                                            path = asset.downloadUrl,
                                            modifier = AssetDownloadCardStyle.toModifier()
                                                .shutterSnap(isVisible, 450 + (index * 100))
                                        ) {
                                            Row(modifier = AssetIconAndTextStyle.toModifier()) {
                                                Image(
                                                    src = asset.iconPath,
                                                    alt = asset.title,
                                                    modifier = AssetIconStyle.toModifier()
                                                )
                                                Column {
                                                    Div(
                                                        attrs = AssetTitleStyle.toModifier().toAttrs {
                                                            ref { htmlElement ->
                                                                htmlElement.innerHTML = asset.title
                                                                onDispose {}
                                                            }
                                                        })

                                                    Div(
                                                        attrs = AssetDescStyle.toModifier().toAttrs {
                                                            ref { htmlElement ->
                                                                htmlElement.innerHTML = asset.description
                                                                onDispose {}
                                                            }
                                                        })
                                                }
                                            }
                                            if (asset.downloadUrl.isNotBlank()) Box(modifier = DownloadArrowStyle.toModifier()) {
                                                Text(
                                                    "↓"
                                                )
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

// ==========================================
// CMS DATA MODEL & ARCHITECTURE
// ==========================================
/**
 * CMS CONTENT & IMAGE UPLOAD SPECS FOR PRESS KIT:
 * * @field companyBoilerplate (inside NewsPressKitData):
 * - Format: Standard Markdown / injected HTML string.
 * - Content Guidance: Keep to a single comprehensive paragraph summarizing the corporate mission,
 * the MyHand® System technology, and global impact metrics.
 * * @field iconPath (inside DownloadableAssetData):
 * - Aspect Ratio: 1:1 (Perfect Square).
 * - Recommended Dimensions: 48x48px.
 * - Format Constraints: SVG ONLY. Must be monochromatic (primary teal or dark slate) line-art
 * representing the file type (e.g., document/PDF icons, vector branding boxes, ZIP archives).
 * - Max File Size: Under 15KB.
 * * @field downloadUrl (inside DownloadableAssetData):
 * - Pathing Constraint: Must point directly to static files hosted inside the `/public` root folder
 * (e.g., `/documents/IRegained_Brand_Assets.zip`).
 * - Behavior: External URLs or dynamic server strings will fail to trigger the browser's native
 * file-download protocols.
 */
data class DownloadableAssetData(
    val title: String,
    val description: String,
    val iconPath: String,
    val downloadUrl: String // Path to a .zip or .pdf file on your server
)

data class NewsPressKitData(
    val sectionId: String? = null,
    val sectionTitle: String = "PRESS & MEDIA KIT",
    val sectionSubtitle: String = "Access official brand assets, high-resolution hardware photography, and our corporate boilerplate for media publications.",
    val companyBoilerplate: String = "IRegained is a pioneering Digital Neurotherapeutics (DTx) company bridging the gap between neuroplasticity and motor control recovery for functional independence. Through our proprietary MyHand® System—that combines precision mechatronics with intelligent digital therapeutics application—we empower clinical facilities to deliver clinically effective, targeted, function-oriented hand rehabilitation. Headquartered in Sudbury, Ontario, we are committed to eradicating physical limitations for the 15 million individuals affected by stroke globally every year.",
    val mediaContactEmail: String = activeStrings.emailText,
    val assets: List<DownloadableAssetData> = listOf(
        DownloadableAssetData(
            title = "Product Spec Sheet",
            description = "Technical dimensions, power, and clinical specs",
            iconPath = Res.Images.News.Kit.PRODUCT_SPEC,
            downloadUrl = "resources/#resources-brochure-download"
        ), DownloadableAssetData(
            title = "Brand Assets & Logos",
            description = "Vector logos and brand guidelines",
            iconPath = Res.Images.News.Kit.IR_ASSETS,
            downloadUrl = "contact/#contact-patient-form-section"
        ), DownloadableAssetData(
            title = "Product Videos",
            description = "Technical demonstration and clinical specs",
            iconPath = Res.Images.News.Kit.IR_VIDEOS,
            downloadUrl = "contact/#contact-patient-form-section"
        )
    )
)

val defaultNewsPressKitData = NewsPressKitData()